import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  NodeMouseHandler,
  Panel,
  useReactFlow,
  Edge,
  Node,
  Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { CustomNode, FloatingConnectionLine, FloatingEdge } from './components';
import { ButtonGroup } from '..';
import { useExpandCollapse } from './hooks';

import {
  ButtonActionType,
  MindNodeEmun,
  MinMapColorMode,
  NodesModeButtons,
  ScreenModeButtons,
  ZoomButtons,
} from './components/const';

export type MindMapProps = {
  className?: string;
  data: { nodes: Node[]; edges: Edge[] };
  fullScreenMode?: boolean;
  onFullScreen?: () => void;
};

const proOptions = { hideAttribution: true };

const edgeTypes = {
  floating: FloatingEdge,
};

const nodeTypes = {
  custom: CustomNode,
};

export const MindMap: React.FC<MindMapProps> = ({
  className,
  data,
  fullScreenMode = false,
  onFullScreen,
}) => {
  const [globalExpanded, setGlobalExpanded] = useState<boolean | undefined>(
    undefined
  );
  const [flowKey, setFlowKey] = useState<number>(0);

  const { zoomIn, zoomOut, setCenter } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(data.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(data.edges);

  useEffect(() => {
    setNodes(data.nodes);
    setEdges(data.edges);
    setFlowKey(prev => prev + 1);
  }, [data, data.edges, data.nodes, setEdges, setNodes]);

  const { nodes: visibleNodes, edges: visibleEdges } = useExpandCollapse(
    nodes,
    edges,
    globalExpanded
  );

  const screenModeButton = useMemo(
    () => ScreenModeButtons.filter(b => b.state === fullScreenMode),
    [fullScreenMode]
  );

  const nodesModeButton = useMemo(
    () => NodesModeButtons.filter(b => b.state === !!globalExpanded),
    [globalExpanded]
  );

  const onConnect = useCallback(
    (params: Connection) => {
      return setEdges(eds =>
        addEdge(
          {
            ...params,
            type: 'floating',
            markerEnd: { type: MarkerType.Arrow },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const handleExpandCollapseAll = useCallback(() => {
    setGlobalExpanded(prev => {
      const newState = prev === undefined ? true : !prev;
      setNodes(nds =>
        nds.map(node =>
          node.id !== MindNodeEmun.PARENT
            ? { ...node, data: { ...node.data, expanded: newState } }
            : node
        )
      );
      return newState;
    });
  }, [setGlobalExpanded, setNodes]);

  const getAllDescendantIds = useCallback(
    (nodeId: string, edgesList: Edge[]): string[] => {
      const descendants = new Set<string>();

      function traverse(id: string) {
        for (const edge of edgesList) {
          if (edge.source === id && !descendants.has(edge.target)) {
            descendants.add(edge.target);
            traverse(edge.target);
          }
        }
      }

      traverse(nodeId);
      return Array.from(descendants);
    },
    []
  );

  const handleClickNode = useCallback(
    (currentNode: Node) => {
      if (globalExpanded !== undefined) {
        setGlobalExpanded(undefined);
      }
      if (currentNode.id === MindNodeEmun.PARENT) return;

      const descendantIds = getAllDescendantIds(currentNode.id, edges);

      setNodes(nds =>
        nds.map(node => {
          if (node.id === currentNode.id || descendantIds.includes(node.id)) {
            return {
              ...node,
              data: { ...node.data, expanded: !currentNode.data?.expanded },
            };
          }
          return node;
        })
      );
    },
    [globalExpanded, setNodes, edges, getAllDescendantIds]
  );

  const onNodeClick: NodeMouseHandler = useCallback(
    (_, node) => {
      setCenter(node.position.x, node.position.y, {
        duration: 1000,
        zoom: 1,
      });
      handleClickNode(node as Node);
    },
    [handleClickNode, setCenter]
  );

  const handleFullScreenMode = useCallback(() => {
    onFullScreen?.();
    setFlowKey(prev => prev + 1);
  }, [onFullScreen]);

  const onButtonClick = useCallback(
    (action: ButtonActionType) => {
      switch (action) {
        case ButtonActionType.EXPAND:
          handleFullScreenMode();
          break;
        case ButtonActionType.ZOOM_IN:
          zoomIn();
          break;
        case ButtonActionType.ZOOM_OUT:
          zoomOut();
          break;
        case ButtonActionType.COLLAPSE:
          handleFullScreenMode();
          break;
        case ButtonActionType.NODES_EXPAND:
          handleExpandCollapseAll();
          break;
        case ButtonActionType.NODES_COLLAPSE:
          handleExpandCollapseAll();
          break;
        default:
          break;
      }
    },
    [handleExpandCollapseAll, handleFullScreenMode, zoomIn, zoomOut]
  );

  return (
    <div className="w-full h-full">
      <ReactFlow
        key={flowKey}
        colorMode={MinMapColorMode.DARK}
        nodes={visibleNodes as Node[]}
        edges={visibleEdges as Edge[]}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        proOptions={proOptions}
        fitView
        fitViewOptions={{ maxZoom: 1 }}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionLineComponent={FloatingConnectionLine}
        className={className}
      >
        <Panel position="top-left">
          <ButtonGroup
            buttons={nodesModeButton}
            orientation="horizontal"
            buttonClassName="p-3"
            onClick={action => onButtonClick(action)}
          />
        </Panel>
        <Panel position="top-right">
          <ButtonGroup
            buttons={screenModeButton}
            orientation="horizontal"
            buttonClassName="p-3"
            onClick={action => onButtonClick(action)}
          />
        </Panel>
        <Panel position="bottom-right">
          <ButtonGroup
            buttons={ZoomButtons}
            orientation="vertical"
            onClick={action => onButtonClick(action)}
          />
        </Panel>
      </ReactFlow>
    </div>
  );
};
